import { useCallback } from 'react';
import { resetParameterState } from './resetParameterState.ts';
import { useDeferredFieldUpdate } from './useDeferredFieldUpdate.ts';

export function useRemoveParameterUpdater<
  State extends Record<PropertyKey, any>,
  Option extends keyof State,
  Api,
  Field,
>(params: {
  field: Field;
  onFieldUpdate: (field: Field, value: Api | null) => void;
  setState: (updater: (prev: State) => State) => void;
  getDefaults: () => State;
  mapStateToApi: (state: State) => Api | null;
  afterSetState?: (next: State, option: Option, defaults: State) => void;
}) {
  const { field, onFieldUpdate, setState, getDefaults, mapStateToApi, afterSetState } = params;

  // Use shared hook for deferred field updates
  const scheduleDeferredUpdate = useDeferredFieldUpdate(field, onFieldUpdate, mapStateToApi);

  return useCallback(
    (option: Option) => {
      setState(prev => {
        const defaults = getDefaults();
        const next = resetParameterState(prev, option, defaults);

        afterSetState?.(next, option, defaults);

        // Schedule the field update to happen after setState completes
        scheduleDeferredUpdate(next);

        return next;
      });
    },
    [setState, getDefaults, afterSetState, scheduleDeferredUpdate]
  );
}
